import { useAuthContext } from '@/context'
import { cn } from '@/lib/utils'
import { queryClient } from '@/queryClient'
import { useStopImitation } from '@/services/auth/mutations'
import { findActiveBusinessAccount } from '@/utils/business'
import { Building2, ChevronsUpDown, Loader2, Network } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { BusinessesDropdown } from './BusinessesDropdown'
import SwitchWorkspace from './SwitchWorkspace'

export function MainNav({ className, ...props }: React.HTMLAttributes<HTMLElement>) {
    const navigate = useNavigate()
    const { user, businessAccounts, activeBusiness, orgAccount } = useAuthContext()

    const isActiveBusiness = findActiveBusinessAccount(businessAccounts, activeBusiness)

    const { mutate: stopImitation, isLoading: stoppingImitation } = useStopImitation({
        onSuccess: () => {
            navigate(`/dashboard/flows/my-workflows`)
            queryClient.invalidateQueries()
        },
    })

    return (
        <nav className={cn('flex items-center space-x-2 lg:space-x-6', className)} {...props}>
            <div className='flex items-center gap-2 sm:gap-3'>
                <BusinessesDropdown
                    trigger={
                        <div className='flex items-center gap-1.5 p-1.5 sm:p-2 border border-[#EAA8923D] rounded-lg cursor-pointer'>
                            <span className='bg-[#FDF1EC] p-1.5 rounded-full'>
                                <Building2 size={13} className='text-[#E04E15]' />
                            </span>
                            {/* Name + label — hidden on mobile */}
                            <aside className='hidden sm:block'>
                                <p className='text-xs font-medium truncate max-w-[120px] text-neutral-charcoal'>
                                    {isActiveBusiness?.business.name}
                                </p>
                                <p className='text-[10px] font-medium text-[#808080]'>Active business</p>
                            </aside>
                            <ChevronsUpDown size={13} className='text-gray-400' />
                        </div>
                    }
                />

                <span className='text-gray-400 text-sm'>/</span>

                <SwitchWorkspace
                    trigger={
                        <div className='flex items-center gap-1.5 p-1.5 sm:p-2 border border-[#EAA8923D] rounded-lg cursor-pointer'>
                            <span className='bg-[#FDF1EC] p-1.5 rounded-full'>
                                <Network size={13} className='text-[#E04E15]' />
                            </span>
                            {/* Name + label — hidden on mobile */}
                            <aside className='hidden sm:block'>
                                <p className='text-xs font-medium truncate max-w-[120px] text-neutral-charcoal'>
                                    {orgAccount?.org?.name || 'Workspace'}
                                </p>
                                <p className='text-[10px] font-medium text-[#808080]'>Active workspace</p>
                            </aside>
                            <ChevronsUpDown size={13} className='text-gray-400' />
                        </div>
                    }
                />
            </div>

            {user?.imitation?.originalUserId && (
                <Link
                    onClick={() => stopImitation({})}
                    to='#'
                    className='text-sm font-medium transition-colors hover:text-primary-main'
                >
                    {stoppingImitation ? <Loader2 className='w-4 h-4 mr-2 animate-spin' /> : 'Stop Imitation'}
                </Link>
            )}
        </nav>
    )
}
